import type { definitions } from '@luxuryescapes/contract-svc-accommodation'

type ServerEnquirySession = definitions['EnquirySession']
type ServerEnquirySessionData = definitions['EnquirySessionData']
type ServerEnquirySessionBundle = NonNullable<ServerEnquirySessionData['bundle']>
function mapSelectedRate(
  selectedRate: ServerEnquirySessionData['supplierMetaData']['selectedRate'],
): App.AccommodationEnquirySessionSelectedRate {
  return {
    optionId: selectedRate.optionId,
    ...(selectedRate.internalRateId != null ? { internalRateId: selectedRate.internalRateId } : {}),
    ...(selectedRate.ratePlanId != null ? { ratePlanId: selectedRate.ratePlanId } : {}),
    ...(selectedRate.rateGroupId != null ? { rateGroupId: selectedRate.rateGroupId } : {}),
    ...(selectedRate.bedGroupId != null ? { bedGroupId: selectedRate.bedGroupId } : {}),
  }
}

function mapSupplierMetaData(
  supplierMetaData: ServerEnquirySessionData['supplierMetaData'],
): App.AccommodationEnquirySessionSupplierMetaData {
  return {
    internalService: supplierMetaData.internalService,
    selectedRate: mapSelectedRate(supplierMetaData.selectedRate),
    ...(supplierMetaData.internalPropertyId != null
      ? { internalPropertyId: supplierMetaData.internalPropertyId }
      : {}),
    ...(supplierMetaData.internalRoomId != null
      ? { internalRoomId: supplierMetaData.internalRoomId }
      : {}),
  }
}

function mapBundle(
  bundle: ServerEnquirySessionBundle,
): App.AccommodationEnquirySessionBundle {
  return {
    bundleId: bundle.bundleId,
    tickets: bundle.tickets.map(ticket => ({
      experienceId: ticket.experienceId,
      productId: ticket.productId,
      ...(ticket.ticketPrice != null ? { ticketPrice: ticket.ticketPrice } : {}),
    })),
    ...(bundle.optIn === true ? { optIn: true } : {}),
  }
}

function splitSessionBundles(data: ServerEnquirySessionData): {
  bundle?: App.AccommodationEnquirySessionBundle
  optInBundles?: Array<App.AccommodationEnquirySessionBundle>
} {
  const optInBundles = data.optInBundles?.map(optIn => mapBundle({ ...optIn, optIn: true }))

  return {
    ...(data.bundle != null ? { bundle: mapBundle(data.bundle) } : {}),
    ...(optInBundles?.length ? { optInBundles } : {}),
  }
}

function mapEnquirySessionData(data: ServerEnquirySessionData): App.AccommodationEnquirySessionData {
  const { occupancy, pricing } = data

  return {
    pricing: {
      currency: pricing.currency,
      sellTotal: pricing.sellTotal,
      ...(pricing.propertyFees != null ? { propertyFees: pricing.propertyFees } : {}),
      ...(pricing.surchargePayableAtProperty != null
        ? { surchargePayableAtProperty: pricing.surchargePayableAtProperty }
        : {}),
      ...(pricing.luxPlusTotal != null ? { luxPlusTotal: pricing.luxPlusTotal } : {}),
    },
    property: { propertyId: data.property.propertyId },
    room: { roomId: data.room.roomId },
    occupancy: {
      adults: occupancy.adults,
      ...(occupancy.children != null ? { children: occupancy.children } : {}),
      ...(occupancy.teenagers != null ? { teenagers: occupancy.teenagers } : {}),
      ...(occupancy.infants != null ? { infants: occupancy.infants } : {}),
      ...(occupancy.childrenAges?.length ? { childrenAges: occupancy.childrenAges } : {}),
    },
    supplierMetaData: mapSupplierMetaData(data.supplierMetaData),
    checkIn: data.checkIn,
    checkOut: data.checkOut,
    brand: data.brand,
    region: data.region,
    currency: data.currency,
    ...(data.orderId != null ? { orderId: data.orderId } : {}),
    ...(data.orderItemId != null ? { orderItemId: data.orderItemId } : {}),
    ...(data.enquiryId != null ? { enquiryId: data.enquiryId } : {}),
    ...(data.transactionKey != null ? { transactionKey: data.transactionKey } : {}),
    ...(data.loyaltyPackageUpgradeDiscount != null
      ? { loyaltyPackageUpgradeDiscount: data.loyaltyPackageUpgradeDiscount }
      : {}),
    ...splitSessionBundles(data),
  }
}

/**
 * Maps a svc-accommodation EnquirySession response into the customer-portal domain type.
 */
export function mapAccommodationEnquirySession(
  session: ServerEnquirySession,
): App.AccommodationEnquirySession {
  return {
    id: session.id,
    source: session.source,
    expiresAt: session.expiresAt,
    createdAt: session.createdAt,
    updatedAt: session.updatedAt,
    ...(session.orderId != null ? { orderId: session.orderId } : {}),
    ...(session.orderItemId != null ? { orderItemId: session.orderItemId } : {}),
    ...(session.transactionKey != null ? { transactionKey: session.transactionKey } : {}),
    ...(session.enquiryId != null ? { enquiryId: session.enquiryId } : {}),
    data: mapEnquirySessionData(session.data),
  }
}
